import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import RankChange from './RankChange';
import { CompetitorBadges } from './CompetitorBadge';

const statusBorderColors = {
  new: 'var(--color-info)',
  active: 'var(--color-success)',
  more_evidence_needed: 'var(--color-warning)',
  under_consideration: 'var(--color-ai)',
  in_progress: 'var(--coros-red)',
  acknowledged: 'var(--text-muted)',
  shipped: 'var(--color-success)',
  denied: 'var(--color-error)',
};

function isNew(createdAt) {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return created > sevenDaysAgo;
}

function CopyLinkButton({ clusterId }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/clusters/${clusterId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy link'}
      aria-label={copied ? 'Copied!' : 'Copy link'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 28, height: 28, borderRadius: 6,
        background: copied ? 'var(--color-success-bg)' : 'var(--bg-tertiary)',
        border: '1px solid var(--border-color)',
        color: copied ? 'var(--color-success)' : 'var(--text-muted)',
        cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0,
      }}
    >
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      )}
    </button>
  );
}

export function ClusterCard({ cluster, showRank = true }) {
  const [hovered, setHovered] = useState(false);
  const hasCompetitors = cluster.competitor_mentions && cluster.competitor_mentions.length > 0;
  const borderColor = statusBorderColors[cluster.status] || statusBorderColors.new;
  const isNewCluster = isNew(cluster.created_at);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Link
        to={`/clusters/${cluster.id}`}
        style={{
          display: 'flex', alignItems: 'start', justifyContent: 'space-between',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-color)',
          borderLeft: `3px solid ${borderColor}`,
          borderRadius: '0 12px 12px 0',
          padding: '16px 20px',
          textDecoration: 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          boxShadow: hovered ? 'var(--shadow-md)' : 'var(--shadow-xs)',
        }}
      >
        {/* Left: Rank + Content */}
        <div style={{ display: 'flex', alignItems: 'start', gap: 16, flex: 1, minWidth: 0 }}>
          {/* Rank block */}
          {showRank && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--coros-red)', lineHeight: 1 }}>
                #{cluster.rank}
              </span>
              <RankChange change={cluster.rank_change} />
            </div>
          )}

          {/* Content block */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Name row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <h3 style={{
                margin: 0, fontSize: 16, fontWeight: 700,
                color: 'var(--text-primary)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {cluster.name}
              </h3>
              {isNewCluster && (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: 'var(--coros-red)',
                  background: 'var(--coros-red-light)',
                  padding: '1px 6px', borderRadius: 4, flexShrink: 0,
                }}>
                  NEW
                </span>
              )}
              {cluster.is_emerging_trend && (
                <span style={{ fontSize: 13, flexShrink: 0 }}>🔥</span>
              )}
            </div>

            {/* Status + Competitors row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
              <StatusBadge status={cluster.status} size="xs" />
              {hasCompetitors && (
                <>
                  <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>·</span>
                  <CompetitorBadges competitors={cluster.competitor_mentions} max={2} />
                </>
              )}
            </div>

            {/* Sample quote — 2 lines */}
            {cluster.sample_quote && (
              <p style={{
                margin: 0, fontSize: 13, fontStyle: 'italic',
                color: 'var(--text-muted)',
                borderLeft: '2px solid var(--coros-red-muted)',
                paddingLeft: 10,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>
                "{cluster.sample_quote}"
              </p>
            )}
          </div>
        </div>

        {/* Right: Count + Trend + Copy */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: 16, flexShrink: 0, gap: 4 }}>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>
              {cluster.ticket_count}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>requests</span>
          </div>
          {cluster.trend_percentage != null && cluster.trend_percentage !== 0 && (
            <span style={{
              fontSize: 12, fontWeight: 700,
              color: cluster.trend_percentage > 0 ? 'var(--color-success)' : 'var(--color-error)',
            }}>
              {cluster.trend_percentage > 0 ? '▲ +' : '▼ '}{cluster.trend_percentage}%
            </span>
          )}
          {hovered && <CopyLinkButton clusterId={cluster.id} />}
        </div>
      </Link>
    </div>
  );
}

export default ClusterCard;
